import {Button, Dropdown, Form, Input, TextArea} from "semantic-ui-react";
import {IItem} from "../../interfaces/Item";
import {align, calcTextWidth, rotationAngles, round} from "../../utils/constants";
import {getDefaultTextValue} from "../../utils/stringTagList";
import {useSelector} from "react-redux";
import {RootState} from "../../app/store";
import {ITag} from "../../interfaces/Tag";
import {useTranslation} from "react-i18next";

interface Props {
  style: any;
  selectedItem: IItem;
  pxInMM: number;
  setOpenFontModal: (open: boolean) => void;
  onChangeWithStyle: (propName: string, propValue: any, propNameStyle: string, propValueStyle: any) => void;
  onChange: (propName: string, propValue: any) => void;
  handleAddNewRecord?: () => void;
  role: string;
}

export const VariableTextForm = ({style, selectedItem, pxInMM, setOpenFontModal, onChangeWithStyle, onChange, handleAddNewRecord, role}: Props) => {
  const {dpi, showDefaultValues} = useSelector((state: RootState) => state.labelEdit.present.labelParams);
  const {t} = useTranslation();

  const alignOptions = [{key: align.left, text: t("left"), value: align.left},
                        {key: align.center, text: t("center"), value: align.center},
                        {key: align.right, text: t("right"), value: align.right}];

  const textTags = useSelector((state: RootState) => state.refData.textTags);
  const stringTagListDD = textTags.map((tag: ITag) => {
    return {
      key: tag.id,
      text: tag.name,
      value: tag.id,
    };
  });
  return (
    <Form style={style.form}>
      <Form.Field>
        <label style={{textAlign: "left"}}>{t("variable_text_name")}:</label>
        <div style={{display: "flex", alignItems: "start", width: "100%"}}>
          <Dropdown
            size="small"
            options={stringTagListDD}
            value={selectedItem.name}
            fluid
            selection
            search={(options, query) =>
              options.filter((option) => option.text?.toString().toLowerCase().includes(query.toLowerCase()) || option.value?.toString().toLowerCase().includes(query.toLowerCase()))
            }
            onChange={(e, data) => {
              onChange("name", data.value);
              onChange("defaultText", getDefaultTextValue(textTags, data.value));
            }}
          />
          {(role === "admin" || role === "super_admin") && <Button onClick={handleAddNewRecord} className="icon" icon="plus" basic color="blue" style={{marginLeft: 4}} />}
        </div>
      </Form.Field>
      <Form.Field>
        <label style={{textAlign: "left"}}>{t("default_text")}:</label>
        <TextArea
          rows={1}
          value={selectedItem.defaultText}
          placeholder={t("enter_default_value_here")}
          onChange={(e) => {
            onChange("defaultText", e.target.value);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("x_from")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={round(selectedItem.xInMM)}
          onChange={(e) => {
            onChangeWithStyle("xInMM", Number(e.target.value), "left", `${Number(e.target.value) * pxInMM}px`);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("y_from")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={round(selectedItem.yInMM)}
          onChange={(e) => {
            onChangeWithStyle("yInMM", Number(e.target.value), "top", `${Number(e.target.value) * pxInMM}px`);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("width")}:</label>
        <Input
          fluid
          size="small"
          style={{width: "100%"}}
          type="number"
          value={round(selectedItem.width)}
          onChange={(e) => {
            onChange("width", Number(e.target.value));
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("rotation")}:</label>
        <Dropdown
          size="small"
          options={rotationAngles}
          value={Number(selectedItem.rotation)}
          fluid
          selection={true}
          search={(options, query) =>
            options.filter((option) => option.text?.toString().toLowerCase().includes(query.toLowerCase()) || option.value?.toString().toLowerCase().includes(query.toLowerCase()))
          }
          onChange={(e, data) => {
            onChangeWithStyle("rotation", Number(data.value), "transform", `rotate(${data.value}deg)`);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("text_wrap")}:</label>
        <Form.Checkbox
          checked={selectedItem.wrap ? true : false}
          onChange={(e, data) => {
            onChange("wrap", data.checked);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label></label>
        <Button size="small" compact onClick={() => setOpenFontModal(true)} type="button">
          {t("font_btn")}
        </Button>
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label>{t("alignment")}:</label>
        <Dropdown
          size="small"
          options={alignOptions}
          value={selectedItem.align}
          fluid
          selection={true}
          search={(options, query) =>
            options.filter((option) => option.text?.toString().toLowerCase().includes(query.toLowerCase()) || option.value?.toString().toLowerCase().includes(query.toLowerCase()))
          }
          onChange={(e, data) => {
            onChange("align", data.value);
          }}
        />
      </Form.Field>
      <Form.Field inline style={style.field}>
        <label></label>
        <Button
          compact
          size="small"
          onClick={() => {
            onChange("width", calcTextWidth(showDefaultValues ? selectedItem.defaultText || "" : selectedItem.name || "", selectedItem.fontSize || 10, selectedItem.fontName || "Arial", dpi, selectedItem.fontBold ? 'bold' : 'normal'));
          }}
        >
          {t("set_width")}
        </Button>
      </Form.Field>
      <Form.Field inline style={style.field}>
        {/* <label>{t("bring_to_back")}:</label>
        <Form.Checkbox
          checked={selectedItem.bringBack === 1 ? true : false}
          onChange={(e, data) => {
            onChange("bringBack", selectedItem.bringBack === 1 ? 2 : 1);
          }}
        /> */}
        <Button
          compact fluid basic 
          onClick={() => {
            onChange("bringBack", selectedItem.bringBack === 1 ? 2 : 1);
          }}
        >
          {t("bring_to_back")}
        </Button>
      </Form.Field>
    </Form>
  );
};
